import React from 'react';
import {
    ScatterChart,
    Scatter,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell,
    ReferenceLine,
    Label
} from 'recharts';
import { getCategoryColor } from 'config/ideaCategories';

const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        const idea = payload[0].payload;
        return (
            <div className="bg-black text-white px-4 py-3 rounded-xl shadow-lg max-w-xs">
                <p className="font-semibold text-sm mb-1">{idea.title}</p>
                <p className="text-white/70 text-xs mb-2">{idea.categoryLabel}</p>
                <div className="flex gap-4 text-xs">
                    <span>Impatto: <strong>{idea.impact}</strong></span>
                    <span>Facilità: <strong>{idea.ease}</strong></span>
                </div>
            </div>
        );
    }
    return null;
};

export default function CompanyMappingMatrix({ ideas = [], onSelectIdea }) {
    // Transform ideas data for scatter chart
    // X-axis: ease (facilità) - 1-10 where 10 is easiest
    // Y-axis: impact - 1-10 where 10 is highest impact
    const chartData = ideas.map(idea => ({
        ...idea,
        x: idea.ease || (10 - idea.difficulty + 1), // Convert difficulty to ease if needed
        y: idea.impact,
        ease: idea.ease || (10 - idea.difficulty + 1),
        color: getCategoryColor(idea.category)
    }));

    return (
        <div className="w-full">
            <div className="relative" style={{ height: '400px' }}>
                <ResponsiveContainer width="100%" height="100%">
                    <ScatterChart
                        margin={{ top: 40, right: 40, bottom: 40, left: 40 }}
                    >
                        <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="rgba(0,0,0,0.1)"
                        />
                        <XAxis
                            type="number"
                            dataKey="x"
                            domain={[0, 10]}
                            tick={false}
                        >
                            <Label
                                value="FACILITÀ DI IMPLEMENTAZIONE"
                                position="bottom"
                                offset={10}
                                style={{ fill: 'rgba(0,0,0,0.5)', fontSize: 11, fontWeight: 500 }}
                            />
                        </XAxis>
                        <YAxis
                            type="number"
                            dataKey="y"
                            domain={[0, 10]}
                            tick={false}
                        >
                            <Label
                                value="IMPATTO AZIENDALE"
                                angle={-90}
                                position="insideLeft"
                                offset={10}
                                style={{ fill: 'rgba(0,0,0,0.5)', fontSize: 11, fontWeight: 500, textAnchor: 'middle' }}
                            />
                        </YAxis>

                        {/* Quadrant dividers */}
                        <ReferenceLine
                            x={5}
                            stroke="rgba(0,0,0,0.15)"
                            strokeWidth={2}
                        />
                        <ReferenceLine
                            y={5}
                            stroke="rgba(0,0,0,0.15)"
                            strokeWidth={2}
                        />

                        <Tooltip content={<CustomTooltip />} />

                        <Scatter
                            data={chartData}
                            onClick={(data) => onSelectIdea?.(data)}
                        >
                            {chartData.map((entry, index) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={entry.color}
                                    stroke="white"
                                    strokeWidth={2}
                                    r={8}
                                    cursor="pointer"
                                />
                            ))}
                        </Scatter>
                    </ScatterChart>
                </ResponsiveContainer>

                {/* Quadrant Labels */}
                <div className="absolute top-12 left-12 text-xs font-semibold text-black/20 uppercase tracking-wider">
                    Major Projects
                </div>
                <div className="absolute top-12 right-12 text-xs font-semibold text-black/20 uppercase tracking-wider">
                    Quick Wins
                </div>
                <div className="absolute bottom-16 left-12 text-xs font-semibold text-black/20 uppercase tracking-wider">
                    Thankless Tasks
                </div>
                <div className="absolute bottom-16 right-12 text-xs font-semibold text-black/20 uppercase tracking-wider">
                    Fill-ins
                </div>
            </div>
        </div>
    );
}
